import { useOpQuery } from 'utils/customHooks/useOpQuery';
import { getAccessToken } from 'utils/accessToken';
import { getActiveScopes } from 'utils/scopes';
import { getOpParams } from 'utils/getOpParams';
import { OpRawMenuItemType } from 'config/createRawMenuItems';

import { getFirstAvailableVisitorRoute } from '../getFirstAvailableVisitorRoute';

/**
 * Resolves the first visitor page the current user is allowed to access, based
 * on the sidebar menu items and their active scopes. Uses the same token/org
 * lookups as `useValidateScopes` so scope resolution is consistent.
 *
 * @param menuItems - the visitor sidebar items to evaluate, in display order.
 * @returns An object containing:
 *   - `route`: the first accessible relative route, or `null` if none.
 *   - `isLoading`: whether the data required for resolution is still loading.
 */
export const useFirstAvailableVisitorRoute = (
  menuItems: OpRawMenuItemType[],
) => {
  const { orgId } = getOpParams();
  const accessToken = getAccessToken();

  const { data: orgData, isPending: isOrgIdPending } = useOpQuery({
    apiEndpointName: 'describeOrg',
    parameters: [orgId],
    select: (data) => data.json.data,
  });

  const { data: tokenData, isPending: isTokenPending } = useOpQuery({
    apiEndpointName: 'describeAccessToken',
    parameters: [accessToken],
    select: (data) => data.json.data,
  });

  const orgScopes = getActiveScopes({
    tokenScopeList: tokenData?.tokenScopeList ?? [],
    orgId: Number(orgId),
    parentOrgId: orgData?.parentOrg?.id,
  });

  const partnerScopes = getActiveScopes({
    tokenScopeList: tokenData?.tokenScopeList ?? [],
    parentOrgId: orgData?.parentOrg?.id,
  });

  const activeScopes = [...orgScopes, ...partnerScopes];

  const isLoading = Boolean(accessToken) && (isOrgIdPending || isTokenPending);

  return {
    route: isLoading
      ? null
      : getFirstAvailableVisitorRoute(menuItems, activeScopes, Number(orgId)),
    isLoading,
  };
};
